import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { backdropClose } from '../lib/modalBackdrop';

const VIEWPORT = 280; // размер круглого окна редактора (css px)
const OUTPUT = 512; // размер итогового квадратного изображения (px)

interface Props {
  file: File;
  onCancel: () => void;
  onSave: (blob: Blob) => void;
}

/** Редактор аватара: перемещение (drag) и зум (слайдер), обрезка по кругу, экспорт в JPEG. */
export default function AvatarEditor({ file, onCancel, onSave }: Props) {
  const { t } = useTranslation('misc');
  const [imgUrl, setImgUrl] = useState<string | null>(null);
  const [natural, setNatural] = useState({ w: 0, h: 0 });
  const [zoom, setZoom] = useState(1);
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const [saving, setSaving] = useState(false);
  const imgRef = useRef<HTMLImageElement>(null);
  const dragRef = useRef<{ startX: number; startY: number; origX: number; origY: number } | null>(null);

  useEffect(() => {
    const url = URL.createObjectURL(file);
    setImgUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  // "Cover"-масштаб — картинка целиком закрывает круглое окно при zoom=1.
  const baseScale = natural.w && natural.h ? Math.max(VIEWPORT / natural.w, VIEWPORT / natural.h) : 1;
  const scale = baseScale * zoom;
  const dispW = natural.w * scale;
  const dispH = natural.h * scale;

  // Не даём картинке отойти от края так, чтобы в окне появилась пустота.
  const clamp = (p: { x: number; y: number }, w: number, h: number) => ({
    x: Math.min(0, Math.max(VIEWPORT - w, p.x)),
    y: Math.min(0, Math.max(VIEWPORT - h, p.y)),
  });

  const onImgLoad = () => {
    const img = imgRef.current;
    if (!img) return;
    const w = img.naturalWidth;
    const h = img.naturalHeight;
    setNatural({ w, h });
    const bs = Math.max(VIEWPORT / w, VIEWPORT / h);
    setPos({ x: (VIEWPORT - w * bs) / 2, y: (VIEWPORT - h * bs) / 2 });
  };

  const onZoomChange = (z: number) => {
    setZoom(z);
    const s = baseScale * z;
    setPos((p) => clamp(p, natural.w * s, natural.h * s));
  };

  const onPointerDown = (e: React.PointerEvent) => {
    (e.currentTarget as Element).setPointerCapture(e.pointerId);
    dragRef.current = { startX: e.clientX, startY: e.clientY, origX: pos.x, origY: pos.y };
  };
  const onPointerMove = (e: React.PointerEvent) => {
    if (!dragRef.current) return;
    const dx = e.clientX - dragRef.current.startX;
    const dy = e.clientY - dragRef.current.startY;
    setPos(clamp({ x: dragRef.current.origX + dx, y: dragRef.current.origY + dy }, dispW, dispH));
  };
  const onPointerUp = () => {
    dragRef.current = null;
  };

  const save = () => {
    const img = imgRef.current;
    if (!img || !natural.w) return;
    setSaving(true);
    const canvas = document.createElement('canvas');
    canvas.width = OUTPUT;
    canvas.height = OUTPUT;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      setSaving(false);
      return;
    }
    // Окно VIEWPORTxVIEWPORT в координатах экрана -> прямоугольник в координатах исходной картинки.
    const sx = -pos.x / scale;
    const sy = -pos.y / scale;
    const sSize = VIEWPORT / scale;
    ctx.drawImage(img, sx, sy, sSize, sSize, 0, 0, OUTPUT, OUTPUT);
    canvas.toBlob(
      (blob) => {
        setSaving(false);
        if (blob) onSave(blob);
      },
      'image/jpeg',
      0.92,
    );
  };

  return (
    <div className="modal-backdrop" {...backdropClose(onCancel)}>
      <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 360 }}>
        <h2>{t('avatarEditorTitle')}</h2>
        <div
          className="avatar-editor-viewport"
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerLeave={onPointerUp}
        >
          {imgUrl && (
            <img
              ref={imgRef}
              src={imgUrl}
              alt=""
              onLoad={onImgLoad}
              draggable={false}
              style={{ left: pos.x, top: pos.y, width: dispW || undefined, height: dispH || undefined }}
            />
          )}
        </div>
        <div className="avatar-editor-zoom-row">
          <span style={{ fontSize: 13, color: 'var(--muted)' }}>−</span>
          <input
            type="range"
            min={1}
            max={3}
            step={0.01}
            value={zoom}
            onChange={(e) => onZoomChange(Number(e.target.value))}
            className="avatar-editor-zoom"
          />
          <span style={{ fontSize: 13, color: 'var(--muted)' }}>+</span>
        </div>
        <div className="row" style={{ justifyContent: 'flex-end', marginTop: 12 }}>
          <button type="button" className="btn btn-ghost" onClick={onCancel}>{t('avatarEditorCancel')}</button>
          <button type="button" className="btn" disabled={saving || !imgUrl} onClick={save}>
            {saving ? t('avatarEditorSaving') : t('avatarEditorSave')}
          </button>
        </div>
      </div>
    </div>
  );
}
